import AddToDashboardButton from './AddToDashboardButton'
import AnalysisCardTitle from './AnalysisCardTitle'
import EffectVisualization from '../dataVisualizations/EffectVisualization'
import ParentCompareView from '../dataVisualizations/ParentCompareView'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'
import { EmptyStateProvider } from '../../StateManagment/EmptyStateContext'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// AI-CONTEXT: See docs/features/compare-two-periods.md
// Thin wrapper around `EffectVisualization` — adds the dashboard-specific
// header and the per-indicator pin button. All visualisation logic (filter
// bar, navigator, IndicatorData, DashboardCardOptions) lives in the shared
// component.
//
// Compare mode (compareActive + rangeA/rangeB): the header renders once and a
// single shared navigator drives both periods, via ParentCompareView — the
// chart renders twice, its own chrome suppressed (hideControls).

// Analyse/dashboard cards show the empty-state CTA in their charts.
const EMPTY_STATE_CTX = { type: 'effect', showCta: true }

/**
 * @param {object} props
 * @param {object} props.effect
 * @param {string} [props.titleLink] - Path to the deep analysis; makes the title a link (Analyse page only)
 * @param {boolean} [props.compareActive] - Render two periods side by side
 * @param {{start: string|null, end: string|null}|null} [props.rangeA] - Primary period (also the single-period filter when not comparing)
 * @param {{start: string|null, end: string|null}|null} [props.rangeB] - Compare period
 */
const AnalysisEffectCardBody = ({
    effect,
    headerAction,
    titleLink,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    targetgroupFilter,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    onClearFilters,
    researchIdsOverride,
    filterByResearchId,
    dashboardItem = null,
    showOptions = false,
    graphTypeByIndicator,
    colorOverridesByIndicator,
    onSaveIndicatorOptions,
    hidePins = false,
    compareActive = false,
    rangeA = null,
    rangeB = null,
}) => {
    // Analyse page: no DashboardItems doc exists, so synthesise one from the
    // card's transient option state. The graph-type / colour picker reads the
    // same map fields the dashboard doc uses; edits save back via onSaveOptions.
    const optionsItem = showOptions
        ? {
            GraphTypeByIndicator: graphTypeByIndicator || {},
            ColorOverridesByIndicator: colorOverridesByIndicator || {},
        }
        : null

    // Deep-analysis page (hidePins): the page's anchor header already shows the
    // effect title, so the in-view header is dropped here.
    const headerEl = !hidePins ? (
        <div className="new-dashboard-card-label analysis-card-header">
            <div className="analysis-card-header-text">
                <AnalysisCardTitle title={effect.Effect} titleLink={titleLink} />
            </div>
            {headerAction || (
                <AddToDashboardButton
                    type='effect'
                    effectId={effect.ID}
                    label={effect.Effect}
                    showLabel
                />
            )}
        </div>
    ) : null

    // Renders one period's EffectVisualization. `nav` is present only in compare
    // mode: it suppresses the viz chrome (hideControls) and wires the shared
    // navigator (controlled visible + item-list reporting).
    const renderViz = (range, nav) => {
        const { startDate, endDate } = periodRangeToDates(range)
        const compare = !!nav
        return (
            <EffectVisualization
                effect={effect}
                graphTypeOverride={graphTypeOverride}
                colorPalette={colorPalette}
                colorOverrides={colorOverrides}
                targetgroupFilter={targetgroupFilter}
                personas={personas}
                personasEnabled={personasEnabled}
                selectedPersonaId={selectedPersonaId}
                onPersonaChange={onPersonaChange}
                onClearFilters={onClearFilters}
                researchIdsOverride={researchIdsOverride}
                filterByResearchId={filterByResearchId}
                dashboardItem={dashboardItem || optionsItem}
                onSaveOptions={showOptions ? onSaveIndicatorOptions : undefined}
                startDate={startDate}
                endDate={endDate}
                periodRange={range}
                hideControls={compare}
                visible={compare ? nav.visible : undefined}
                onVisibleChange={compare ? nav.setVisible : undefined}
                onItemsChange={compare ? nav.setItems : undefined}
                renderItemAction={(hidePins || compare) ? undefined : (currentField, currentEffect) => (
                    <AddToDashboardButton
                        type='indicator'
                        effectId={currentEffect.ID}
                        indicatorId={currentField.ID}
                        label={currentField.Question || 'Vraag'}
                    />
                )}
            />
        )
    }

    return (
        <EmptyStateProvider value={EMPTY_STATE_CTX}>
            {compareActive ? (
                <ParentCompareView
                    header={headerEl}
                    rangeA={rangeA}
                    rangeB={rangeB}
                    renderViz={(range, side, nav) => renderViz(range, nav)}
                />
            ) : (
                <>
                    {headerEl}
                    {renderViz(rangeA, null)}
                </>
            )}
        </EmptyStateProvider>
    )
}

export default AnalysisEffectCardBody
